import { Component, signal, inject, CUSTOM_ELEMENTS_SCHEMA, computed } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { RouterOutlet, RouterLink, RouterModule } from '@angular/router';
import { NgFor, NgIf, NgClass } from '@angular/common';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  route: string;
  badge?: {
    text: string;
    color: string;
  };
  children?: MenuItem[];
  requiredRoles?: string[];
  requiredPermissions?: string[];
}

@Component({
  selector: 'app-private-layout',
  templateUrl: './private-layout.component.html',
  styleUrls: ['./private-layout.component.css'],
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterModule, NgFor, NgIf, NgClass],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class PrivateLayoutComponent {
  private router = inject(Router);
  private authService = inject(AuthService);

  currentRoute = signal<string>('');
  sidebarCollapsed = signal<boolean>(false);

  allMenuItems = signal<MenuItem[]>([
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'grid-3x3',
      route: '/dashboard'
    },
    {
      id: 'employees',
      label: 'Employees',
      icon: 'people',
      route: '/employees',
      requiredPermissions: ['VIEW_ALL_EMPLOYEES', 'VIEW_OWN_PROFILE'],
      children: [
        {
          id: 'employees-list',
          label: 'All Employees',
          icon: 'list',
          route: '/employees',
          requiredPermissions: ['VIEW_ALL_EMPLOYEES']
        },
        {
          id: 'employees-add',
          label: 'Add Employee',
          icon: 'person',
          route: '/employees/add',
          requiredRoles: ['HR_ADMIN']
        },
        {
          id: 'employees-org-chart',
          label: 'Org Chart',
          icon: 'diagram-3',
          route: '/employees/org-chart',
          requiredPermissions: ['VIEW_ALL_EMPLOYEES']
        }
      ]
    },
    {
      id: 'timesheet',
      label: 'Timesheet',
      icon: 'clock',
      route: '/timesheet',
      badge: { text: '3', color: 'bg-blue-500' },
      requiredPermissions: ['VIEW_ALL_TIMESHEETS', 'VIEW_OWN_TIMESHEET'],
      children: [
        {
          id: 'timesheet-dashboard',
          label: 'Dashboard',
          icon: 'speedometer2',
          route: '/timesheet/dashboard',
          requiredPermissions: ['VIEW_ALL_TIMESHEETS', 'VIEW_OWN_TIMESHEET']
        },
        {
          id: 'timesheet-weekly',
          label: 'Weekly View',
          icon: 'calendar-week',
          route: '/timesheet/weekly',
          requiredPermissions: ['VIEW_ALL_TIMESHEETS', 'VIEW_OWN_TIMESHEET']
        },
        // {
        //   id: 'timesheet-calendar',
        //   label: 'Calendar',
        //   icon: 'calendar3',
        //   route: '/timesheet/calendar',
        //   requiredRoles: ['HR_ADMIN', 'MANAGER']
        // },
        {
          id: 'timesheet-history',
          label: 'History',
          icon: 'clock-history',
          route: '/timesheet/history',
          requiredRoles: ['HR_ADMIN', 'MANAGER']
        },
        {
          id: 'timesheet-approvals',
          label: 'Approvals',
          icon: 'check-circle',
          route: '/timesheet/approvals',
          requiredPermissions: ['APPROVE_TIMESHEETS']
        },
        {
          id: 'timesheet-statistics',
          label: 'Analytics',
          icon: 'graph-up',
          route: '/timesheet/statistics',
          requiredRoles: ['HR_ADMIN', 'MANAGER']
        }
      ]
    },
    {
      id: 'leave',
      label: 'Leave Management',
      icon: 'calendar-x',
      route: '/leave',
      badge: { text: '8', color: 'bg-orange-500' },
      requiredPermissions: ['VIEW_ALL_LEAVE', 'VIEW_OWN_LEAVE', 'APPLY_LEAVE'],
      children: [
        {
          id: 'leave-dashboard',
          label: 'Dashboard',
          icon: 'speedometer2',
          route: '/leave/dashboard',
          requiredPermissions: ['VIEW_ALL_LEAVE', 'VIEW_OWN_LEAVE', 'APPLY_LEAVE']
        },
        {
          id: 'leave-apply',
          label: 'Apply for Leave',
          icon: 'plus-circle',
          route: '/leave/apply',
          requiredPermissions: ['APPLY_LEAVE']
        },
        {
          id: 'leave-calendar',
          label: 'Leave Calendar',
          icon: 'calendar-days',
          route: '/leave/calendar',
          requiredPermissions: ['VIEW_ALL_LEAVE', 'VIEW_OWN_LEAVE']
        },
        {
          id: 'leave-balance',
          label: 'Leave Balance',
          icon: 'pie-chart',
          route: '/leave/balance',
          requiredPermissions: ['VIEW_ALL_LEAVE', 'VIEW_OWN_LEAVE']
        },
        {
          id: 'leave-history',
          label: 'Leave History',
          icon: 'clock-history',
          route: '/leave/history',
          requiredPermissions: ['VIEW_ALL_LEAVE', 'VIEW_OWN_LEAVE']
        },
        {
          id: 'leave-approvals',
          label: 'Approvals',
          icon: 'check-circle',
          route: '/leave/approvals',
          requiredPermissions: ['APPROVE_LEAVE']
        },
        {
          id: 'leave-adjustments',
          label: 'Adjustments',
          icon: 'arrow-up-circle',
          route: '/leave/adjustments',
          requiredPermissions: ['VIEW_ALL_LEAVE', 'VIEW_OWN_LEAVE']
        },
        {
          id: 'leave-analytics',
          label: 'Analytics',
          icon: 'graph-up',
          route: '/leave/analytics',
          requiredRoles: ['HR_ADMIN', 'MANAGER']
        },
        {
          id: 'leave-configuration',
          label: 'Configuration',
          icon: 'gear',
          route: '/leave/configuration',
          requiredRoles: ['HR_ADMIN']
        }
      ]
    },
    {
      id: 'rota-planner',
      label: 'Rota Planner',
      icon: 'calendar-week',
      route: '/rota-planner',
      requiredRoles: ['HR_ADMIN', 'MANAGER', 'EMPLOYEE'],
      children: [
        {
          id: 'rota-planner-weekly',
          label: 'Weekly Planner',
          icon: 'calendar3',
          route: '/rota-planner/planner',
          requiredRoles: ['HR_ADMIN', 'MANAGER']
        },
        {
          id: 'rota-planner-my-schedule',
          label: 'My Schedule',
          icon: 'person-calendar',
          route: '/rota-planner/my-schedule',
          requiredRoles: ['HR_ADMIN', 'MANAGER', 'EMPLOYEE']
        }
      ]
    },
    {
      id: 'performance',
      label: 'Performance',
      icon: 'bar-chart',
      route: '/performance',
      // requiredPermissions: ['VIEW_ALL_PERFORMANCE', 'VIEW_OWN_PERFORMANCE'],
      // requiredPermissions: ['VIEW_ALL_EMPLOYEES'],
      requiredRoles: ['HR_ADMIN', 'MANAGER', 'EMPLOYEE'],
      children: [
        {
          id: 'performance-dashboard',
          label: 'Dashboard',
          icon: 'speedometer2',
          route: '/performance/dashboard',
          requiredRoles: ['HR_ADMIN', 'MANAGER', 'EMPLOYEE']
        },
        {
          id: 'performance-reviews',
          label: 'Performance Reviews',
          icon: 'star',
          route: '/performance/reviews',
          requiredRoles: ['HR_ADMIN', 'MANAGER', 'EMPLOYEE']
        },
        {
          id: 'performance-goals',
          label: 'Goals',
          icon: 'target',
          route: '/performance/goals',
          requiredRoles: ['HR_ADMIN', 'MANAGER', 'EMPLOYEE']
        },
        {
          id: 'performance-analytics',
          label: 'Analytics',
          icon: 'graph-up',
          route: '/performance/analytics',
          requiredRoles: ['HR_ADMIN', 'MANAGER']
        }
      ]
    },
    {
      id: 'documents',
      label: 'Documents',
      icon: 'file-earmark-text',
      route: '/documents',
      requiredRoles: ['HR_ADMIN', 'MANAGER', 'EMPLOYEE'],
      children: [
        {
          id: 'documents-dashboard',
          label: 'Dashboard',
          icon: 'speedometer2',
          route: '/documents/dashboard',
          requiredRoles: ['HR_ADMIN', 'MANAGER', 'EMPLOYEE']
        },
        {
          id: 'my-documents',
          label: 'My Documents',
          icon: 'person-badge',
          route: '/documents/my-documents',
          requiredRoles: ['HR_ADMIN', 'MANAGER', 'EMPLOYEE']
        },
        {
          id: 'company-documents',
          label: 'Company Documents',
          icon: 'building',
          route: '/documents/company-documents',
          requiredRoles: ['HR_ADMIN', 'MANAGER', 'EMPLOYEE']
        },
        {
          id: 'upload-document',
          label: 'Upload Document',
          icon: 'cloud-upload',
          route: '/documents/upload',
          requiredRoles: ['HR_ADMIN', 'MANAGER', 'EMPLOYEE']
        },
        {
          id: 'document-management',
          label: 'Document Management',
          icon: 'gear',
          route: '/documents/management',
          requiredRoles: ['HR_ADMIN']
        }
      ]
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: 'card-list',
      route: '/reports',
      requiredRoles: ['HR_ADMIN'],
      children: [
        { id: 'reports-analytics', label: 'Analytics', icon: 'pie-chart', route: '/reports/analytics' },
        { id: 'reports-payroll', label: 'Payroll', icon: 'credit-card', route: '/reports/payroll' },
        { id: 'reports-attendance', label: 'Attendance', icon: 'user-check', route: '/reports/attendance' },
        { id: 'reports-custom', label: 'Custom Reports', icon: 'gear', route: '/reports/custom' }
      ]
    }
  ]);

  // Computed property to filter menu items based on user permissions
  menuItems = computed(() => {
    return this.filterMenuItems(this.allMenuItems());
  });

  expandedMenus = signal<Set<string>>(new Set());

  constructor() {
    // Track current route
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute.set(event.url);
      });
  }

  // Filter menu items based on user roles and permissions
  private filterMenuItems(items: MenuItem[]): MenuItem[] {
    return items.filter(item => this.hasAccessToMenuItem(item))
                .map(item => ({
                  ...item,
                  children: item.children ? this.filterMenuItems(item.children) : undefined
                }))
                .filter(item => !item.children || item.children.length > 0); // Remove parent items with no accessible children
  }

  private hasAccessToMenuItem(item: MenuItem): boolean {
    // If no restrictions, allow access
    if (!item.requiredRoles && !item.requiredPermissions) {
      return true;
    }

    // Check roles
    if (item.requiredRoles && item.requiredRoles.length > 0) {
      if (this.authService.hasAnyRole(item.requiredRoles)) {
        return true;
      }
    }

    // Check permissions
    if (item.requiredPermissions && item.requiredPermissions.length > 0) {
      if (this.authService.hasAnyPermission(item.requiredPermissions)) {
        return true;
      }
    }

    return false;
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.set(!this.sidebarCollapsed());
  }

  toggleMenu(menuId: string): void {
    const expanded = new Set(this.expandedMenus());
    if (expanded.has(menuId)) {
      expanded.delete(menuId);
    } else {
      expanded.add(menuId);
    }
    this.expandedMenus.set(expanded);
  }

  isMenuExpanded(menuId: string): boolean {
    return this.expandedMenus().has(menuId);
  }

  isActiveRoute(route: string): boolean {
    const current = this.currentRoute();
    return current === route || current.startsWith(route + '/') || current.startsWith(route + '?');
  }

  navigateToRoute(route: string): void {
    this.router.navigate([route]);
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  getCurrentPageTitle(): string {
    const currentRoute = this.currentRoute();

    // Find the matching menu item
    for (const item of this.menuItems()) {
      if (this.isActiveRoute(item.route)) {
        // Check if it's a child route
        if (item.children) {
          for (const child of item.children) {
            if (this.isActiveRoute(child.route)) {
              return child.label;
            }
          }
        }
        return item.label;
      }
    }

    // Default titles for specific routes
    if (currentRoute.includes('/add')) return 'Add New';
    if (currentRoute.includes('/edit')) return 'Edit';
    if (currentRoute.includes('/view')) return 'View Details';

    return 'Dashboard';
  }
}
